/**
 * Aggregated dirty-state for the draft (workflow + connections + code buffer).
 *
 * Every save-related surface needs the same question answered: "does the
 * draft have unsaved changes?". Centralising it here keeps the call sites
 * (autosave loop, status badge, manual save button, keyboard shortcut
 * handler) in lockstep — if we ever add a fourth dirty-tracked artifact
 * (e.g. parameters), this is the only place to update.
 *
 * `codeBufferDirty` covers the case where the user is editing raw JSON in
 * the Code panel and the buffer diverges from the committed workflow tree
 * — including when the JSON transiently fails to parse. Without it, Save
 * silently stays disabled on invalid input with no explanation.
 */

import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';

/** Reactive: subscribes to all dirty signals across the draft. */
export const useIsDraftDirty = (): boolean => {
  const workflowIsDirty = useWorkflowStore((s) => s.isDirty || s.codeBufferDirty);
  const connectionsIsDirty = useConnectionStore((s) => s.isDirty);
  return workflowIsDirty || connectionsIsDirty;
};

/**
 * Non-reactive companion for event handlers that need the current value
 * without re-binding on every dirty toggle (e.g. global Ctrl+S listener).
 */
export const getIsDraftDirty = (): boolean =>
  useWorkflowStore.getState().isDirty ||
  useConnectionStore.getState().isDirty ||
  useWorkflowStore.getState().codeBufferDirty;

/** Unapplied node JSON is user intent, but not yet a persistable artifact. */
export const useHasUnappliedNodeCode = (): boolean =>
  useNodeEditorStore((state) => state.nodeCodeDraft.isDirty);

export const getHasUnappliedNodeCode = (): boolean =>
  useNodeEditorStore.getState().nodeCodeDraft.isDirty;

/** Includes persistable changes plus editor buffers that still need action. */
export const useHasUnsavedDraftIntent = (): boolean => {
  const isDraftDirty = useIsDraftDirty();
  const hasUnappliedNodeCode = useHasUnappliedNodeCode();
  return isDraftDirty || hasUnappliedNodeCode;
};

export const getHasUnsavedDraftIntent = (): boolean =>
  getIsDraftDirty() || getHasUnappliedNodeCode();

export interface BlockingCodeIssue {
  reason:
    | 'workflow_code_parse_error'
    | 'workflow_code_unapplied'
    | 'node_code_unapplied';
  title: string;
  message: string;
}

export const getBlockingCodeIssue = (): BlockingCodeIssue | null => {
  const workflowError = useWorkflowStore.getState().codeBufferParseError;
  if (workflowError) {
    return {
      reason: 'workflow_code_parse_error',
      title: 'Fix JSON errors before saving',
      message: workflowError,
    };
  }

  if (useWorkflowStore.getState().codeBufferDirty) {
    return {
      reason: 'workflow_code_unapplied',
      title: 'Wait for workflow JSON validation',
      message:
        'The whole-workflow Code panel still has unapplied changes. Fix or finish those changes before saving.',
    };
  }

  const nodeDraft = useNodeEditorStore.getState().nodeCodeDraft;
  if (nodeDraft.isDirty) {
    return {
      reason: 'node_code_unapplied',
      title: 'Apply node code changes before saving',
      message:
        nodeDraft.validationError ??
        nodeDraft.conflictError ??
        'Apply or revert the changes in the node editor Code section.',
    };
  }
  return null;
};
